import React, { useMemo } from 'react'
import { PieChart, Pie, Cell } from 'recharts'
import { useTheme } from '@material-ui/styles'
import Theme from 'core/themes/model'

export interface PieDataEntry {
  value: number
  name: string
  color: keyof Theme['components']['graph'] | string
  strokeColor?: keyof Theme['components']['graph'] | string
}

export interface PercentPieGraphProps {
  data: PieDataEntry[]
  width?: number
  height?: number
  sideLength?: number
  arcWidth?: number
  percent?: number
  startAngle?: number
  endAngle?: number
  primary?: string
  empty?: boolean
  healthColor?: PieDataEntry['color']
  percentagePrecision?: number
}

const PercentPieGraph = ({
  data = [],
  sideLength = 216,
  arcWidth = 45,
  percent = undefined,
  primary,
  empty = false,
  healthColor = 'primary',
  startAngle = 0,
  endAngle = 360,
  width = sideLength + 10,
  height = sideLength + 10,
  percentagePrecision = 0,
  ...rest
}: PercentPieGraphProps) => {
  const theme: Theme = useTheme()
  const radius = Math.floor(sideLength / 2)
  const emptyData = useMemo(
    () => [
      {
        name: 'unknown',
        value: 1,
        color: theme.components.graph.tray,
        strokeColor: theme.components.graph.stroke,
      },
    ],
    [theme.components.graph.tray],
  )
  const items = empty ? emptyData : data
  return (
    // @ts-ignore
    <PieChart width={width} height={height} data-testid="percent-pie-graph">
      <Pie
        dataKey="value"
        data={items}
        startAngle={startAngle}
        endAngle={endAngle}
        cx={radius}
        cy={radius}
        innerRadius={radius - arcWidth}
        outerRadius={radius}
        paddingAngle={0}
        {...rest}
      >
        {items.map((entry) => {
          return (
            <>
              <Cell
                key={entry?.name}
                fill={entry?.color}
                stroke={entry?.strokeColor as string}
                strokeWidth={entry.value > 0 ? 1 : 0}
              />
            </>
          )
        })}
      </Pie>
      {percent !== undefined && (
        <text
          style={{ ...theme.typography.h4 }}
          x={radius + 5}
          y={radius}
          fill={theme.components.card.text}
          textAnchor="middle"
          dominantBaseline="middle"
          data-testid="percent-text"
        >
          {`${(percent * 100).toFixed(percentagePrecision)}%`}
        </text>
      )}
      {primary && (
        <text
          style={{ ...theme.typography.body2 }}
          x={radius + 5}
          y={radius + 20}
          fill={theme.components.card.text}
          textAnchor="middle"
          dominantBaseline="middle"
          data-testid="primary-text"
        >
          {primary}
        </text>
      )}
    </PieChart>
  )
}

export default PercentPieGraph
